import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  InputBase,
  IconButton,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { SearchOutlined, Add, Edit, Delete } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  GetAllCerboTags,
  GetAllCRMTags,
  GetAllCustomFields,
  getAllTagMappings,
  deleteTagMapping,
} from "../../Service/AxiosService";
import AddTagMapping from "./AddTagMapping";
import { Chip, Tooltip } from "@mui/material";

const ROW_HEIGHT = 37;

const TagList = () => {
  const theme = useTheme();
  const dataGridWrapperRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editRow, setEditRow] = useState<any | null>(null);
  const [deleteRow, setDeleteRow] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [value, setValue] = React.useState(0);
  const [row1, setRow1] = useState<any[]>([]);

  const [crmtags, setCrmtags] = useState<any[]>([]);
  const [cerbotags, setCerbotags] = useState<any[]>([]);
  const [customfields, setCustomfields] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Once the user explicitly picks a "rows per page" value, stop the viewport
  // auto-fit (below) from overriding their choice.
  const userChosePageSizeRef = useRef(false);


  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  /* -------------------- Dynamic Page Size Logic -------------------- */

  const calculatePageSize = (gridEl: HTMLDivElement) => {
    const header = gridEl.querySelector(".MuiDataGrid-columnHeaders");
    const footer = gridEl.querySelector(".MuiDataGrid-footerContainer");

    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const footerHeight = footer?.getBoundingClientRect().height ?? 0;
    const gridHeight = gridEl.getBoundingClientRect().height;

    const availableHeight = gridHeight - headerHeight - footerHeight;
    const rowsThatFit = Math.floor(availableHeight / ROW_HEIGHT);

    return Math.max(5, rowsThatFit);
  };

  useEffect(() => {
    document.title = `${process.env.REACT_APP_CLINIC_NAME || 'Clinic'} - Tags`;

    if (!dataGridWrapperRef.current) return;

    const observer = new ResizeObserver(() => {
      if (userChosePageSizeRef.current) return; // respect the user's manual choice
      const newPageSize = calculatePageSize(dataGridWrapperRef.current!);

      setPaginationModel((prev) => {
        if (prev.pageSize === newPageSize) return prev;
        return { ...prev, pageSize: newPageSize, page: 0 };
      });
    });

    observer.observe(dataGridWrapperRef.current);

    return () => observer.disconnect();
  }, []);

  // Offer common page sizes plus the current (viewport-fit) size so the user can
  // choose how many rows to show per page. The active size must be included, or
  // MUI DataGrid warns that the current pageSize is not among the options.
  const pageSizeOptions = useMemo(
    () =>
      Array.from(new Set([paginationModel.pageSize, 10, 25, 50, 100])).sort(
        (a, b) => a - b,
      ),
    [paginationModel.pageSize],
  );

  const handlePaginationModelChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationModel((prev) => {
      if (model.pageSize !== prev.pageSize) {
        userChosePageSizeRef.current = true;
      }
      return model;
    });
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      row.addCrmTag?.toLowerCase().includes(term) ||
      row.removeCrmTag?.toLowerCase().includes(term) ||
      row.CerboAddTags?.toLowerCase().includes(term) ||
      row.CerboRemoveTags?.toLowerCase().includes(term) ||
      row.customFieldId?.toLowerCase().includes(term) ||
      row.customFieldValue?.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const handleEdit = (row: any) => {
    setEditRow(row);
    setOpenAddDialog(true);
  };

  const handleDelete = (row: any) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteRow?.id) {
      await deleteTagMapping(deleteRow.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
    }
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  const fetchTagMappings = async () => {
    setLoading(true);
    try {
      const [tagMappingsRes, crmTagsRes, cerboTagsRes, customFieldsRes] =
        await Promise.all([
          getAllTagMappings(),
          GetAllCRMTags(),
          GetAllCerboTags(),
          GetAllCustomFields(),
        ]);

      const rawCrm = crmTagsRes?.data ?? crmTagsRes;
      const crmList: any[] = Array.isArray(rawCrm)
        ? rawCrm
        : Array.isArray(rawCrm?.data)
        ? rawCrm.data
        : [];

      const rawCerbo = cerboTagsRes?.data ?? cerboTagsRes;
      const cerboList: any[] = Array.isArray(rawCerbo)
        ? rawCerbo
        : Array.isArray(rawCerbo?.data)
        ? rawCerbo.data
        : [];

      const rawCustom = customFieldsRes?.data ?? customFieldsRes;
      const customList: any[] = Array.isArray(rawCustom)
        ? rawCustom
        : Array.isArray(rawCustom?.data)
        ? rawCustom.data
        : [];

      setCrmtags(crmList);
      setCerbotags(cerboList);
      setCustomfields(customList);
      // Normalize tag mappings response to an array to avoid runtime errors
      const rawTagData = tagMappingsRes?.data ?? tagMappingsRes;
      let tempRows: any[] = Array.isArray(rawTagData)
        ? rawTagData
        : Array.isArray(rawTagData?.data)
        ? rawTagData.data
        : [];

      if (value === 0) {
        tempRows = tempRows.filter((r: any) => !r.isCerbo);
      } else if (value === 1) {
        tempRows = tempRows.filter((r: any) => r.isCerbo);
      }
      const mappedRows = tempRows?.map((a: any, i: number) => ({
          id: a.id,
          sNo: i + 1,
          // Raw ids for the edit form to initialise from (robust — the display-name
          // re-derivation below is only for the table and must not drive persistence).
          addCrmTagId: a.addCrmTagId ?? null,
          removeCrmTagId: a.removeCrmTagId ?? null,
          addCrmTag: crmList.find((t: any) => t.tagId === a.addCrmTagId)
            ?.tagName,
          removeCrmTag: crmList.find((t: any) => t.tagId === a.removeCrmTagId)
            ?.tagName,
          CerboAddTags: cerboList
            .filter((t: any) => (a.addCerboTagId?.split(",") || []).includes(t.id))
            .map((t: any) => `${t.tagCategory} - ${t.name}`)
            .join(", "),
          CerboRemoveTags: cerboList
            .filter((t: any) => (a.removeCerboTagId?.split(",") || []).includes(t.id))
            .map((t: any) => `${t.tagCategory} - ${t.name}`)
            .join(", "),
          hasValue: a.hasValue ? "Yes" : "No",
          isFromCerbo: a.isCerbo ? "Cerbo Tag" : "HL Tag",
          isAdd: a.isAdd ? "Add" : "Remove",
          customFieldId:
            customList.find((f: any) => f.fieldId === a.customFieldId)
              ?.fieldName ?? "",
          customFieldValue: a.customFieldValue ?? "",
        })) ?? [];

      setRows(mappedRows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTagMappings();
  }, [value]);

  const columns: GridColDef[] = [];
  if (value === 0) {
    columns.push(
      {
        field: "sNo",
        headerName: "S.No",
        width: 80,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "addCrmTag",
        headerName: "HL Add Tag",
        width: 120,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "isAdd",
        headerName: "Conditions",
        width: 130,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const isAdd = params.value === "Add";
          return (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  height: 24,
                  px: 1.5,
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex", // 👈 change here
                  alignItems: "center", // 👈 vertical center
                  justifyContent: "center", // 👈 horizontal center
                  color: isAdd ? "#15803d" : "#b91c1c",
                  backgroundColor: isAdd ? "#dcfce7" : "#fee2e2",
                }}
              >
                {params.value}
              </Box>
            </Box>
          );
        },
      },
      // {
      //   field: "removeCrmTag",
      //   headerName: "GHL Remove Tag",
      //   width: 120,
      //   headerAlign: "left",
      //   align: "left",
      // },
      // {
      //   field: "isFromCerbo",
      //   headerName: "Tag Source",
      //   width: 130,
      //   headerAlign: "left",
      //   align: "left",
      // },
      {
        field: "CerboAddTags",
        headerName: "Cerbo Add Tags",
        flex: 1,
        minWidth: 150,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const tags = params.value
            ? params.value
              .split(",")
              .filter(Boolean)
              .map((t: string) => t.trim())
            : [];
          const tagString = tags.join(", ");
          if (!tagString) return null;
          return (
            <Tooltip title={tagString} placement="top" arrow>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  width: "100%",
                }}
              >
                {tagString}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "CerboRemoveTags",
        headerName: "Cerbo Remove Tags",
        flex: 1,
        minWidth: 150,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const tags = params.value
            ? params.value
              .split(",")
              .filter(Boolean)
              .map((t: string) => t.trim())
            : [];
          const tagString = tags.join(", ");
          if (!tagString) return null;
          return (
            <Tooltip title={tagString} placement="top" arrow>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  width: "100%",
                }}
              >
                {tagString}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "hasValue",
        headerName: "Tag Notes",
        width: 120,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "customFieldId",
        headerName: "Customfield id",
        width: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "customFieldValue",
        headerName: "Customfield Value",
        width: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              aria-label="edit"
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <Edit sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              size="small"
              onClick={() => handleDelete(params.row)}
            >
              <Delete sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        ),
      },
    )
   
  } else {
    columns.push(
      {
        field: "sNo",
        headerName: "S.No",
        width: 80,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "CerboAddTags",
        headerName: "Cerbo Add Tags",
        flex: 1,
        minWidth: 150,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const tags = params.value
            ? params.value
              .split(",")
              .filter(Boolean)
              .map((t: string) => t.trim())
            : [];
          const tagString = tags.join(", ");
          if (!tagString) return null;
          return (
            <Tooltip title={tagString} placement="top" arrow>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  width: "100%",
                }}
              >
                {tagString}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "isAdd",
        headerName: "Conditions",
        width: 130,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const isAdd = params.value === "Add";
          return (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  height: 24,
                  px: 1.5,
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex", // 👈 change here
                  alignItems: "center", // 👈 vertical center
                  justifyContent: "center", // 👈 horizontal center
                  color: isAdd ? "#15803d" : "#b91c1c",
                  backgroundColor: isAdd ? "#dcfce7" : "#fee2e2",
                }}
              >
                {params.value}
              </Box>
            </Box>
          );
        },
      },

      {
        field: "addCrmTag",
        headerName: "HL Add Tag",
        width: 120,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "removeCrmTag",
        headerName: "HL Remove Tag",
        width: 120,
        headerAlign: "left",
        align: "left",
      },
      // {
      //   field: "isFromCerbo",
      //   headerName: "Tag Source",
      //   width: 130,
      //   headerAlign: "left",
      //   align: "left",
      // },


      // {
      //   field: "CerboRemoveTags",
      //   headerName: "Cerbo Remove Tags",
      //   width: 200,
      //   headerAlign: "left",
      //   align: "left",
      //   renderCell: (params) => {
      //     const tags = params.value
      //     ? params.value
      //           .split(",")
      //           .filter(Boolean)
      //           .map((t: string) => t.trim())
      //       : [];
      //     const tagString = tags.join(", ");
      //     if (!tagString) return null;
      //     return (
      //       <Tooltip title={tagString} placement="top" arrow>
      //         <span
      //           style={{
      //             whiteSpace: "nowrap",
      //             overflow: "hidden",
      //             textOverflow: "ellipsis",
      //             display: "block",
      //             maxWidth: 180,
      //           }}
      //         >
      //           {tagString}
      //         </span>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        field: "hasValue",
        headerName: "Tag Notes",
        width: 120,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "customFieldId",
        headerName: "Customfield id",
        width: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "customFieldValue",
        headerName: "Customfield Value",
        width: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              aria-label="edit"
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <Edit sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              size="small"
              onClick={() => handleDelete(params.row)}
            >
              <Delete sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        ),
      },
    )
  }

  /* -------------------- Render -------------------- */

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={value}
        onChange={handleChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          minHeight: 40,
          borderBottom: 'none',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        <Tab label="HL Mapping"
          sx={{
            textTransform: 'capitalize',
            borderRadius: '8px 8px 0px 0px',
            minHeight: 36,
            px: 3,
            mr: 1,
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: '#2b396e',
              color: '#fff',
            },
          }} />
        <Tab label="Cerbo Mapping"
          sx={{
            textTransform: 'capitalize',
            borderRadius: '8px 8px 0px 0px',
            minHeight: 36,
            px: 3,
            mr: 1,
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: '#2b396e',
              color: '#fff',
            },
          }} />
      </Tabs>

      <Box sx={{ p: 2, background: '#fff', border: '1px solid #dedede', boxShadow: "0 2px 16px rgba(24,26,42,0.08)", }}>
        {value === 0 && (
          <Typography>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                p: 0,
                m: 0,
                bgcolor: "transparent",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <Grid
                container
                alignItems="center"
                justifyContent="end"
                sx={{ mb: 1, mt: 0.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 300,
                      bgcolor: "#fff",
                      height: 36,
                      border: "1px solid #dedede",
                      borderRadius: 2,
                    }}
                  >
                    <InputBase
                      placeholder="Search tags..."
                      inputProps={{ "aria-label": "search tags" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        flex: 1,
                        fontSize: 15,
                        bgcolor: "transparent",
                        px: 2,
                        py: 0.5,
                        height: 32,
                      }}
                    />
                    <IconButton
                      sx={{
                        p: "4px",
                        color: "#23244A",
                        height: 32,
                        width: 32,
                      }}
                      aria-label="search"
                      size="small"
                    >
                      <SearchOutlined fontSize="small" />
                    </IconButton>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add fontSize="small" />}
                    sx={{
                      textTransform: "none",
                      boxShadow: "none",
                      bgcolor: "#3D52A0",
                      "&:hover": { bgcolor: "#31417f" },
                      fontSize: 14,
                      borderRadius: 2,
                      minWidth: 120,
                      py: 1,
                      px: 2,
                      height: 36,
                    }}
                    onClick={() => {
                      setEditRow(null);
                      setOpenAddDialog(true);
                    }}
                  >
                    New Tag Rule
                  </Button>
                </Box>
              </Grid>

              {/* Grid */}
              <Box
                ref={dataGridWrapperRef}
                sx={{
                  width: "100%",
                  height: "calc(100vh - 180px)",
                  pb: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 2px 16px rgba(24,26,42,0.08)",
                }}
              >
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={pageSizeOptions}
                  rowHeight={ROW_HEIGHT}
                  disableRowSelectionOnClick
                  sx={{
                    borderRadius: 3,
                    bgcolor: "#fff",
                    fontSize: 13,
                    flex: 1,
                    boxShadow: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#FAFAFC",
                      fontWeight: 700,
                      fontSize: 13,
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: 12,
                      py: 0,
                      color: "#000",
                    },
                    "& .MuiDataGrid-row": { minHeight: 32, maxHeight: 32 },
                    "& .MuiDataGrid-virtualScroller": {
                      overflow: "hidden !important",
                    },
                    "& .MuiTablePagination-root": {
                      fontSize: 12,
                      borderTop: "1px solid #e0e0e0",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      bgcolor: "#FAFAFC",
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                    },
                    "& .MuiDataGrid-selectedRowCount": { color: "#23244A" },
                    '& .MuiDataGrid-cell[data-field="actions"]': {
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                    },

                    '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#FAFAFC",
                      zIndex: 2,
                    },
                  }}
                />
              </Box>

              {/* Dialogs */}
              <AddTagMapping
                open={openAddDialog}
                onClose={() => {
                  setOpenAddDialog(false);
                  fetchTagMappings();
                }}
                crmTags={crmtags}
                cerboTags={cerbotags}
                customFields={customfields}
                initialValues={editRow}
              />

              <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    background: "#FAFAFC",
                    boxShadow: "0 2px 16px rgba(24,26,42,0.10)",
                    minWidth: 320,
                  },
                }}
              >
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
                  Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ fontSize: 15 }}>
                  Are you sure you want to delete this tag mapping?
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                  <Button
                    onClick={() => setDeleteDialogOpen(false)}
                    color="secondary"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      border: "none",
                      background: "#dedede",
                      color: "#000",
                      textTransform: "capitalize",
                      py: 1,
                      px: 2,
                      "&:hover": { background: "#d6d6d6" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmDelete}
                    color="error"
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      background: "#d32f2f",
                      textTransform: "capitalize",
                      py: 1,
                      px: 2,
                      "&:hover": { background: "#b71c1c" },
                    }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Typography>
        )}
        {value === 1 && (
          <Typography>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                p: 0,
                m: 0,
                bgcolor: "transparent",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <Grid
                container
                alignItems="center"
                justifyContent="end"
                sx={{ mb: 1, mt: 0.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 300,
                      bgcolor: "#fff",
                      height: 36,
                      border: "1px solid #dedede",
                      borderRadius: 2,
                    }}
                  >
                    <InputBase
                      placeholder="Search tags..."
                      inputProps={{ "aria-label": "search tags" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        flex: 1,
                        fontSize: 15,
                        bgcolor: "transparent",
                        px: 2,
                        py: 0.5,
                        height: 32,
                      }}
                    />
                    <IconButton
                      sx={{
                        p: "4px",
                        color: "#23244A",
                        height: 32,
                        width: 32,
                      }}
                      aria-label="search"
                      size="small"
                    >
                      <SearchOutlined fontSize="small" />
                    </IconButton>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add fontSize="small" />}
                    sx={{
                      textTransform: "none",
                      boxShadow: "none",
                      bgcolor: "#3D52A0",
                      "&:hover": { bgcolor: "#31417f" },
                      fontSize: 14,
                      borderRadius: 2,
                      minWidth: 120,
                      py: 1,
                      px: 2,
                      height: 36,
                    }}
                    onClick={() => {
                      setEditRow(null);
                      setOpenAddDialog(true);
                    }}
                  >
                    New Tag Rule
                  </Button>
                </Box>
              </Grid>

              {/* Grid */}
              <Box
                ref={dataGridWrapperRef}
                sx={{
                  width: "100%",
                  height: "calc(100vh - 180px)",
                  pb: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 2px 16px rgba(24,26,42,0.08)",
                }}
              >
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={pageSizeOptions}
                  rowHeight={ROW_HEIGHT}
                  disableRowSelectionOnClick
                  sx={{
                    borderRadius: 3,
                    bgcolor: "#fff",
                    fontSize: 13,
                    flex: 1,
                    boxShadow: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#FAFAFC",
                      fontWeight: 700,
                      fontSize: 13,
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: 12,
                      py: 0,
                      color: "#000",
                    },
                    "& .MuiDataGrid-row": { minHeight: 32, maxHeight: 32 },
                    "& .MuiDataGrid-virtualScroller": {
                      overflow: "hidden !important",
                    },
                    "& .MuiTablePagination-root": {
                      fontSize: 12,
                      borderTop: "1px solid #e0e0e0",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      bgcolor: "#FAFAFC",
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                    },
                    "& .MuiDataGrid-selectedRowCount": { color: "#23244A" },
                    '& .MuiDataGrid-cell[data-field="actions"]': {
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                    },

                    '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#FAFAFC",
                      zIndex: 2,
                    },
                  }}
                />
              </Box>

              {/* Dialogs */}
              <AddTagMapping
                open={openAddDialog}
                onClose={() => {
                  setOpenAddDialog(false);
                  fetchTagMappings();
                }}
                crmTags={crmtags}
                cerboTags={cerbotags}
                customFields={customfields}
                initialValues={editRow}
              />

              <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    background: "#FAFAFC",
                    boxShadow: "0 2px 16px rgba(24,26,42,0.10)",
                    minWidth: 320,
                  },
                }}
              >
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
                  Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ fontSize: 15 }}>
                  Are you sure you want to delete this tag mapping?
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                  <Button
                    onClick={() => setDeleteDialogOpen(false)}
                    color="secondary"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      border: "none",
                      background: "#dedede",
                      color: "#000",
                      textTransform: "capitalize",
                      py: 1,
                      px: 2,
                      "&:hover": { background: "#d6d6d6" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmDelete}
                    color="error"
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      background: "#d32f2f",
                      textTransform: "capitalize",
                      py: 1,
                      px: 2,
                      "&:hover": { background: "#b71c1c" },
                    }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TagList;
